import { kebabCase } from 'lodash';
import moment from 'moment';
import { nonNullable } from '../../../../utils/commonUtils';
import { GenreItem } from '../../../genres/types';
import { ProgramPlaybackOption } from '../../titles/types/playback-option';
import { BaseTitle, EpisodeTitle, MovieTitle, SeriesTitle, PlayableTitle } from '../../titles/types/title';
import { Recording, SeriesRecording, EpisodeRecording, MovieRecording, SingleRecording } from '../types/recording';

export function createSingleRecordingTitle(recording: SingleRecording, genres: GenreItem[]): PlayableTitle {
  if ('episode' in recording && recording.episode !== undefined) {
    return createEpisodeRecordingTitle(recording, genres);
  }

  return createMovieRecordingTitle(recording, genres);
}

export function createSeriesRecordingTitle(series: SeriesRecording, genres: GenreItem[]): SeriesTitle {
  const highestSeason = series.episodes.reduce((acc, ep) => Math.max(acc, ep.season.sequenceNumber), 0);
  const highestAgeRating = series.episodes.reduce((acc, ep) => {
    const rating = parseInt(ep.ageRating, 10);
    return !isNaN(rating) ? Math.max(acc, rating) : 0;
  }, 0);

  return {
    ...createBaseRecordingTitle('series', series, genres),
    episodes: series.episodes.map((ep) => createEpisodeRecordingTitle(ep, genres)),
    masterId: series.seriesId,
    series: {
      masterId: series.seriesId,
      titleName: series.name,
      seasonCount: highestSeason,
      episodeCount: series.episodes.length,
    },
    season: null,
    episode: null,
    ageRating: highestAgeRating,
    playbackOptions: [
      {
        id: series.channelId,
        sourceId: '',
        providerName: series.channelName,
        providerIdentifier: series.channelId,
        distributor: '',
        links: {
          native: null,
          web: null,
        },
        casting: {
          airPlay: true,
          chromecast: true,
        },
        price: [],
        hasTrailer: false,
      },
    ],
  };
}

export function createRecordingEpisodeTitles(series: SeriesRecording, genres: GenreItem[]): EpisodeTitle[] {
  return series.episodes.map((ep) => createEpisodeRecordingTitle(ep, genres));
}

export function createEpisodeRecordingTitle(recording: EpisodeRecording, genres: GenreItem[]): EpisodeTitle {
  return {
    ...createBaseRecordingTitle('episode', recording, genres),
    titleName: recording.name,
    series: {
      masterId: recording.series.id,
      titleName: recording.series.name,
      seasonCount: 0,
      episodeCount: 0,
    },
    season: {
      titleName: `Season ${recording.season.sequenceNumber}`,
      sequenceNumber: recording.season.sequenceNumber,
    },
    episode: {
      masterId: recording.programId,
      titleName: recording.episode.name || `Episode ${recording.episode.sequenceNumber}`,
      sequenceNumber: recording.episode.sequenceNumber,
    },
    playbackOptions: [createRecordingProgramPlaybackOption(recording)],
  };
}

export function createRecordingProgramPlaybackOption(recording: SingleRecording): ProgramPlaybackOption {
  const now = moment();

  return {
    id: '',
    sourceId: '',
    seriesId: recording.series?.id,
    providerName: recording.channelName,
    providerIdentifier: recording.channelId,
    links: {
      native: null,
      web: null,
    },
    casting: {
      airPlay: true,
      chromecast: true,
    },
    price: [],
    hasTrailer: false,
    providerTitleId: '',
    platformTitleId: recording.programId,
    serviceType: 'program',
    media: [],
    subtitleLanguages: [],
    audioLanguages: [],
    distributor: '',
    availableFrom: recording.availableFrom,
    availableTo: recording.availableTo,
    channelName: recording.channelName,
    channelId: recording.channelId,
    startTime: moment(recording.startTime).subtract(recording.startOffset, 'minutes').format(),
    endTime: moment(recording.endTime).add(recording.endOffset, 'minutes').format(),
    supportsCatchup: now.isAfter(recording.availableFrom) && now.isBefore(recording.availableTo),
  };
}

function createMovieRecordingTitle(recording: MovieRecording, genres: GenreItem[]): MovieTitle {
  return {
    ...createBaseRecordingTitle('movie', recording, genres),
    series: null,
    season: null,
    episode: null,
    playbackOptions: [createRecordingProgramPlaybackOption(recording)],
  };
}

function createBaseRecordingTitle<T extends BaseTitle['type']>(
  type: T,
  recording: Recording,
  genres: GenreItem[],
): Omit<BaseTitle, 'series' | 'season' | 'episode'> & { type: T } {
  const single = recording.type === 'series' ? recording.latestEpisode : recording;
  return {
    masterId: single.programId,
    slug: kebabCase(single.name),
    titleName: recording.name,
    genres: single.genres ? createGenres(single.genres, genres) : [],
    productionYear: null,
    flags: {
      norwegianMediaAuthorityId: '0',
    },
    type: type,
    originalTitle: single.originalName,
    descriptionLong: single.description,
    price: null,
    nextStartTime: null,
    providers: [recording.channelId],
    providerNames: [recording.channelName],
    descriptionShort: single.description,
    ageRating: parseInt(single.ageRating, 10),
    cast: [],
    country: single.country,
    tags: [],
    scoreRating: null,
    duration: single.duration,
    orderProvidersBy: {
      type: 'program',
      id: single.channelId,
    },
    __createdFromRecording: true,
  };
}

function createGenres(genreIds: string[], genres: GenreItem[]): string[] {
  return genreIds.map((id) => genres.find((genre) => genre.genreId === id)?.genreName).filter(nonNullable);
}
